import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { useIntl } from 'react-intl';

const UPDATE_USER_MUTATION = gql`
  mutation UpdateCurrentUser($ask_me_about: String) {
    updateCurrentUser(ask_me_about: $ask_me_about)
  }
`;

const AskMeAboutEditModal = ({ user, onClose, onSave, onError }) => {
  const intl = useIntl();
  const [askMeAbout, setAskMeAbout] = useState(user.ask_me_about || '');

  const [updateUser, { loading }] = useMutation(UPDATE_USER_MUTATION, {
    onCompleted: () => onSave('Ask Me About updated successfully'),
    onError: (error) => onError(error),
    refetchQueries: ['GetCurrentUserProfile'],
    awaitRefetchQueries: true,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    updateUser({ variables: { ask_me_about: askMeAbout } });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={onClose}>
      <div className="bg-succession-bg-secondary rounded-lg p-6 max-w-2xl w-full border border-succession-border" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-succession-text-primary">{intl.formatMessage({ id: 'succession.profile_edit.modals.ask_me_about_edit.title' })}</h2>
            <p className="text-succession-text-muted text-base mt-1">{intl.formatMessage({ id: 'succession.profile_edit.modals.ask_me_about_edit.subtitle' })}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-succession-bg-primary rounded-full transition-colors"
          >
            <svg className="w-6 h-6 text-succession-text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="ask_me_about" className="block text-sm font-medium text-succession-text-primary mb-2">
              {intl.formatMessage({ id: 'succession.profile_edit.modals.ask_me_about_edit.topics_label' })}
            </label>
            <textarea
              id="ask_me_about"
              rows="4"
              value={askMeAbout}
              onChange={(e) => setAskMeAbout(e.target.value)}
              className="w-full px-4 py-2 border border-succession-border rounded-lg bg-succession-bg-primary text-succession-text-primary focus:ring-succession-primary focus:border-succession-primary"
              placeholder={intl.formatMessage({ id: 'succession.profile_edit.modals.ask_me_about_edit.topics_placeholder' })}
            />
            <p className="mt-1 text-xs text-succession-text-muted">
              {intl.formatMessage({ id: 'succession.profile_edit.modals.ask_me_about_edit.topics_hint' })}
            </p>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-succession-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-succession-border rounded-lg text-succession-text-primary hover:bg-succession-bg-primary transition-colors"
            >
              {intl.formatMessage({ id: 'succession.profile_edit.common.cancel' })}
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-4 py-2 rounded-lg text-white transition-colors ${
                loading
                  ? 'bg-succession-primary opacity-50 cursor-not-allowed'
                  : 'bg-succession-primary hover:opacity-80'
              }`}
            >
              {loading ? intl.formatMessage({ id: 'succession.profile_edit.common.saving' }) : intl.formatMessage({ id: 'succession.profile_edit.common.save_changes' })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

AskMeAboutEditModal.propTypes = {
  user: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

export default AskMeAboutEditModal;
